import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { TreeService } from '../shared/services/tree.service';
import { Tree } from '../shared/models/tree.model';
import { TranslatePipe } from '../shared/i18n/translate.pipe';
import { AuthService } from '../shared/services/auth.service';
import { UserService } from '../shared/services/user.service';
import { SignupComponent } from '../shared/components/signup/signup.component';
import { PromoCarouselComponent } from './promo-carousel/promo-carousel.component';
import { PricingComponent } from './pricing/pricing.component';
import { LEGAL } from '../legal/legal-data';
import { TreeCreated, TreeEditComponent } from '../user-settings/tree-edit/tree-edit.component';

@Component({
    selector: 'app-main',
    templateUrl: './main.component.html',
    styleUrls: ['./main.component.css'],
    standalone: true,
    imports: [RouterLink, FormsModule, TranslatePipe, PromoCarouselComponent, PricingComponent]
})
export class MainComponent implements OnInit, OnDestroy {
  publicTrees: Tree[] = [];
  loading = true;

  /** Dane kontaktowe w stopce - te same, co na stronach prawnych */
  legal = LEGAL;

  /**
   * Wejście przez /pricing zamiast /. Cennik pokazujemy wtedy ZAWSZE - także
   * zalogowanemu, który na stronie głównej widzi swoje drzewa zamiast oferty.
   * Bez tego adres /pricing prowadziłby zalogowanego donikąd.
   */
  pricingRoute = false;

  /** Jednorazowe zadanie „przewiń do cennika" - kasuje się po wykonaniu. */
  private pendingPricingScroll = false;

  /** Wyszukiwanie osób po wielu drzewach - stąd tylko skok na /search */
  searchQuery = '';
  /**
   * Zalogowany widzi swoje drzewa zamiast reklamy, a publiczne drzewa innych
   * osób niżej - bez własnych, żeby nie dublować kart.
   */
  loggedIn = false;
  myTrees: Tree[] = [];
  loadingMy = false;

  private authSubscription?: Subscription;

  constructor(
    private treeService: TreeService,
    private router: Router,
    private dialog: MatDialog,
    private authService: AuthService,
    private userService: UserService,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    // /pricing to ta sama strona główna, tylko przewinięta do cennika
    this.pricingRoute = this.route.snapshot.data['scrollTo'] === 'pricing';
    this.pendingPricingScroll = this.pricingRoute;

    this.refresh();
    // Zalogowanie/wylogowanie w trakcie oglądania strony przebudowuje ją bez
    // przeładowania (sesja może też wygasnąć sama - patrz AppComponent)
    this.authSubscription = this.authService.authStateChanged.subscribe(() => this.refresh());
  }

  /**
   * Cennik doczytał plany i właśnie się pokazał - dopiero teraz jest do czego
   * przewijać. `setTimeout` daje Angularowi dorysować sekcję: sygnał leci
   * jeszcze przed odświeżeniem widoku, więc bez tego szukalibyśmy elementu,
   * którego nie ma w drzewie dokumentu.
   */
  onPricingLoaded(): void {
    if (!this.pendingPricingScroll) {
      return;
    }
    this.pendingPricingScroll = false;
    setTimeout(() => document.getElementById('pricing')
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
  }

  ngOnDestroy(): void {
    this.authSubscription?.unsubscribe();
  }

  /**
   * Numer telefonu jako odnośnik `tel:` - bez spacji, bo z nimi część
   * telefonów nie potrafi go wybrać. Wyrażenia regularne nie działają
   * w szablonie Angulara, więc składamy adres tutaj.
   */
  get phoneHref(): string {
    return 'tel:' + this.legal.companyPhone.replace(/\s/g, '');
  }

  private refresh(): void {
    this.loggedIn = this.authService.isLoggedIn();
    if (!this.loggedIn) {
      this.myTrees = [];
      this.loadPublicTrees(new Set());
      return;
    }
    this.loadingMy = true;
    this.userService.getUserTrees().subscribe({
      next: (trees) => {
        this.myTrees = (trees ?? [])
          .filter((tree) => !!tree.url)
          .map((tree) => new Tree({ id: tree.tree_id, title: tree.title, url: tree.url }));
        this.loadStats(this.myTrees);
        this.loadingMy = false;
        this.loadPublicTrees(new Set(this.myTrees.map((tree) => tree.id)));
      },
      error: () => {
        this.myTrees = [];
        this.loadingMy = false;
        this.loadPublicTrees(new Set());
      }
    });
  }

  loadPublicTrees(excludeIds: Set<number>): void {
    this.loading = true;
    this.treeService.getTreeList().subscribe({
      next: (trees) => {
        // Backend przysyła już to, co wolno pokazać pytającemu (publiczne,
        // a zalogowanemu też te dla użytkowników serwisu) - zostaje odjęcie
        // własnych drzew, żeby karty się nie dublowały. Losowo do 6.
        const publicTrees = trees.filter(t => !excludeIds.has(t.id)
          && (t.visibility ? t.visibility !== 'private' : t.visiblepublic));
        this.publicTrees = this.shuffleArray(publicTrees).slice(0, 6);
        this.loadStats(this.publicTrees);
        this.loading = false;
      },
      error: (err) => {
        console.error('Error loading trees from API:', err);
        this.loading = false;
      }
    });
  }

  private loadStats(trees: Tree[]): void {
    trees.forEach(tree => {
      this.treeService.getTreeStats(tree.id.toString()).subscribe({
        next: (stats) => {
          tree.person_count = stats.person_count;
        },
        error: (err) => {
          console.error('Error loading tree stats:', err);
        }
      });
    });
  }

  shuffleArray<T>(array: T[]): T[] {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  }

  /** Szukanie zaczyna się tutaj, a dzieje na stronie wyników - stan jest w adresie. */
  onSearch(): void {
    const q = this.searchQuery.trim();
    if (!q) {
      return;
    }
    void this.router.navigate(['/search'], { queryParams: { q } });
  }

  openTree(tree: Tree): void {
    this.router.navigate(['/tree', tree.url]);
  }

  /**
   * Zakładanie drzewa prosto ze strony głównej - to samo okno, co w
   * ustawieniach konta. Nowe drzewo otwieramy od razu, żeby świeży
   * użytkownik nie musiał go szukać.
   */
  createTree(): void {
    this.dialog.open(TreeEditComponent, {
      disableClose: true,
      autoFocus: false
    }).afterClosed().subscribe((tree?: TreeCreated) => {
      if (tree?.url) {
        // z plikiem GEDCOM prosto do importu, inaczej na stronę główną drzewa
        void this.router.navigate(tree.importFirst
          ? ['/tree', tree.url, 'settings', 'import']
          : ['/tree', tree.url]);
      }
    });
  }

  /** Ten sam wzorzec co SigninBoxComponent.onRegister() w nagłówku. */
  onSignup(): void {
    this.dialog.open(SignupComponent, {
      data: { inDialog: true },
      disableClose: true,
      autoFocus: false,
      width: '400px',
      maxWidth: '95vw',
    });
  }
}
